import { toText } from './primitiveHelpers';

export const ORIGIN_KEYS = Object.freeze([
  'origem',
  'origem_lead',
  'source',
  'source_platform',
  'lead_source',
  'lead_origin',
  'portal_origem',
  'utm_source',
  'midia',
  'midia_origem',
  'ad_platform',
  'ad_source',
  'meta_ads_source',
  ['utm', 'source'],
  ['meta', 'source'],
  ['meta_ads', 'source'],
]);

export const CAMPAIGN_KEYS = Object.freeze([
  'campanha',
  'campaign',
  'campaign_name',
  'nome_campanha',
  'utm_campaign',
  'ad_campaign',
  ['utm', 'campaign'],
  ['meta', 'campaign'],
  ['meta_ads', 'campaign'],
]);

export const MEDIA_KEYS = Object.freeze([
  'midia',
  'midia_origem',
  'utm_medium',
  ['utm', 'medium'],
]);

export const CHANNEL_META = Object.freeze({
  whatsapp: {
    label: 'WhatsApp',
    icon: 'i-ph-whatsapp-logo',
    cls: 'bg-emerald-50 text-emerald-700',
  },
  web: {
    label: 'Site / Web',
    icon: 'i-ph-globe',
    cls: 'bg-violet-50 text-violet-700',
  },
  site: {
    label: 'Site / Web',
    icon: 'i-ph-globe',
    cls: 'bg-violet-50 text-violet-700',
  },
  email: {
    label: 'E-mail',
    icon: 'i-ph-envelope',
    cls: 'bg-sky-50 text-sky-700',
  },
  sms: {
    label: 'SMS',
    icon: 'i-ph-chat-text',
    cls: 'bg-amber-50 text-amber-700',
  },
  in_app: {
    label: 'In-app',
    icon: 'i-ph-app-window',
    cls: 'bg-slate-100 text-slate-700',
  },
});

const CHANNEL_TYPE_MAP = Object.freeze({
  ChannelWhatsapp: 'whatsapp',
  ChannelTwilioSms: 'sms',
  ChannelTwilioWhatsapp: 'whatsapp',
  ChannelEmail: 'email',
  ChannelWebWidget: 'web',
  ChannelApi: 'web',
});

const normalizeKey = value =>
  toText(value)
    .replace(/^Channel::/, 'Channel')
    .replace(/::/g, '')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const valueForPath = (attrs, key) => {
  if (Array.isArray(key)) {
    return key.reduce((current, part) => current?.[part], attrs);
  }

  return attrs?.[key];
};

const firstPresent = (attrs, keys) => {
  const key = keys.find(candidate => toText(valueForPath(attrs, candidate)));
  return key ? toText(valueForPath(attrs, key)) : '';
};

export const channelFromType = channelType => {
  const mapped = CHANNEL_TYPE_MAP[toText(channelType).replace(/::/g, '')];
  if (mapped) return mapped;

  const normalized = normalizeKey(channelType);
  if (normalized.includes('whatsapp')) return 'whatsapp';
  if (normalized.includes('email')) return 'email';
  if (normalized.includes('sms')) return 'sms';
  if (normalized.includes('web_widget') || normalized.includes('api'))
    return 'web';
  return '';
};

export const channelMeta = value => {
  const key = normalizeKey(value);
  return (
    CHANNEL_META[key] || {
      label: toText(value) ? toText(value).replaceAll('_', ' ') : 'Origem',
      icon: 'i-ph-tag',
      cls: 'bg-slate-100 text-slate-700',
    }
  );
};

export const resolveContactOrigin = (contact = {}, channelType = '') => {
  const attrs =
    contact.additional_attributes || contact.additionalAttributes || {};
  const explicitChannel = toText(attrs.canal);
  const inferredChannel = explicitChannel ? '' : channelFromType(channelType);
  const channel = explicitChannel || inferredChannel;
  const origem = firstPresent(attrs, ORIGIN_KEYS);
  const campanha = firstPresent(attrs, CAMPAIGN_KEYS);
  const midia = firstPresent(attrs, MEDIA_KEYS);
  const firstTouch =
    toText(attrs.first_touch) ||
    toText(attrs.firstTouch) ||
    toText(contact.created_at || contact.createdAt);
  const meta = channelMeta(channel || origem);

  return {
    canal: channel,
    canalLabel: meta.label,
    canalIcon: meta.icon,
    canalClass: meta.cls,
    origem,
    campanha,
    midia,
    firstTouch,
    inferred: Boolean(inferredChannel),
    hasOrigin: Boolean(channel || origem || campanha || midia || firstTouch),
  };
};

export default resolveContactOrigin;
